// teamUtils.ts

export function defaultDressColor(homeColors: string[], awayColors: string[]) {
  let selectedHomeColor: string | null = null;
  let selectedAwayColor: string | null = null;

  // Pomocná funkce pro získání hlavní barvy (první část před '-')
  const getMainColor = (color: string) => color.split('-')[0];

  // Pokud domácí tým má pouze jednu barvu, vybereme ji jako defaultní
  if (homeColors.length === 1) {
    selectedHomeColor = homeColors[0];

    // Najdeme hostující barvu, která nemá stejnou hlavní barvu
    selectedAwayColor = awayColors.find(color => getMainColor(color) !== getMainColor(selectedHomeColor)) || awayColors[0];
  }
  
  // Pokud hostující tým má pouze jednu barvu, vybereme ji jako defaultní
  else if (awayColors.length === 1) {
    selectedAwayColor = awayColors[0];

    // Najdeme domácí barvu, která nemá stejnou hlavní barvu
    selectedHomeColor = homeColors.find(color => getMainColor(color) !== getMainColor(selectedAwayColor)) || homeColors[0];
  }

  // Pokud oba týmy mají více barev
  else {
    // Domácí tým dostane první barvu
    selectedHomeColor = homeColors[0];

    // Hostující tým dostane barvu, která se liší na hlavní barvě
    selectedAwayColor = awayColors.find(color => getMainColor(color) !== getMainColor(selectedHomeColor)) || awayColors[0];
  }

  // Vrátíme vybrané barvy
  return {
    home: selectedHomeColor,
    away: selectedAwayColor,
  };
}




/**
 * Odstraní číselné suffixy z názvu týmu (např. ročníky).
 * "HCM Moutnice 2013" → "HCM Moutnice"
 * "1. brnenska strojirna" → "1. brnenska strojirna"
 */
export const removeTrailingNumbers = (teamName: string): string => {
  return teamName.replace(/\s+\d+$/, '').trim();
};

/**
 * Funkce, která vrací třípísmennou zkratku daného názvu týmu.
 * Upřednostňuje souhlásky. Pro více slov se vytváří zkratka
 * následujícím způsobem:
 *  - Pokud jsou v názvu 3 a více slov: vezmeme první písmeno ze tří prvních slov.
 *  - Pokud jsou ve jménu 2 slova: vezmeme první dvě písmena z prvního slova (s preferencí souhlásek)
 *    a první písmeno ze druhého slova.
 *  - Pokud jde o jednoslovný název: začneme prvním písmenem a dále vyhledáváme další unikátní souhlásky,
 *    pokud nejsou k dispozici, doplníme první dostupná písmena (v pořadí).
 *
 * @param teamName – název týmu
 * @returns třípísmenná zkratka
 */
export const getShotcut = (teamName: string): string => {
  // Odstraníme případné přebytečné mezery a převedeme celý název na velká písmena.
  if (!teamName || teamName.trim().length === 0) {
    return '???';
  }
  const cleaned = removeTrailingNumbers(teamName);
  const words = cleaned.trim().toUpperCase().split(/\s+/);
  const vowels = 'AEIOU';

  const isConsonant = (char: string): boolean => {
    return /^[A-Z]$/.test(char) && vowels.indexOf(char) === -1;
  };

  let abbreviation = '';

  if (words.length >= 3) {
    // Pokud je ve jménu 3 a více slov, vezmeme první písmeno z prvních tří slov.
    abbreviation = words.slice(0, 3).map(word => word[0]).join('');
  } else if (words.length === 2) {
    // Pro dvouslovný název: vezmeme dvě písmena z prvního slova a první písmeno z druhého.
    const firstWord = words[0];
    const secondWord = words[1];
    let firstPart = '';

    // Vždy začneme prvním písmenem prvního slova.
    firstPart += firstWord[0];

    // Procházíme zbývající písmena prvního slova a vybíráme další souhlásku,
    // pokud je to možné, aby celkem vznikly dvě písmena.
    for (let i = 1; i < firstWord.length && firstPart.length < 2; i++) {
      const char = firstWord[i];
      if (isConsonant(char)) {
        firstPart += char;
      }
    }
    // Pokud souhlásek nebylo dost a zkratka se nevejde jen se souhláskami, doplníme další písmena
    // (i kdyby se jednalo o slabiky/vokály).
    if (firstPart.length < 2) {
      for (let i = 1; i < firstWord.length && firstPart.length < 2; i++) {
        const char = firstWord[i];
        if (!firstPart.includes(char)) {
          firstPart += char;
        }
      }
    }
    abbreviation = firstPart + secondWord[0];
  } else if (words.length === 1) {
    // Jednoslovný název – budeme vybírat první písmeno a další písmena upřednostňující souhlásky.
    const word = words[0];
    abbreviation += word[0]; // první písmeno

    const candidates: string[] = [];

    // Nejprve prozkoumáme zbytek slova a vybíráme souhlásky (bez opakování)
    for (let i = 1; i < word.length; i++) {
      const char = word[i];
      if (isConsonant(char) && !abbreviation.includes(char)) {
        candidates.push(char);
      }
      if (candidates.length >= 2) break;
    }

    // Pokud nebyly nalezeny dvě souhlásky, doplníme další písmena (v pořadí) aby vznikla celková zkratka o délce 3.
    if (candidates.length < 2) {
      for (let i = 1; i < word.length && candidates.length < 2; i++) {
        const char = word[i];
        // Pokud dané písmeno ještě nebylo použito, přidáme ho.
        if (!abbreviation.includes(char) && !candidates.includes(char)) {
          candidates.push(char);
        }
      }
    }
    abbreviation += candidates.slice(0, 2).join('');
  }

  return abbreviation;
};
